import type { AuthUser, FeatureKey } from '@/api/auth';
import { taskLink, type DailyTask } from '@/api/dailyTasks';
import { entryHidden } from '@/lib/aiEntries';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { computeHasFeature } from '@/stores/auth';

/**
 * Куда ведёт строка «Задач на сегодня» у ЭТОГО ученика.
 *
 * ⚠️ В ПРИЛОЖЕНИИ СТРОКА ВЕДЁТ ТОЛЬКО ТУДА, КУДА УЧЕНИК ПОПАДЁТ (03.10.2026, решение владельца к сборке 1.1.1).
 * Задание-раздел несёт путь, который куратор выбрал из списка (`TASK_SECTIONS`), а задаёт он его группе или
 * потоку целиком. В приложении часть разделов сайта не зарегистрирована вовсе, а остальные закрыты разделами
 * ученика: нажатие открыло бы «страница не найдена» либо молча вернуло бы на главную. Теперь переход есть,
 * только если маршрут есть в сборке И открыт ученику его разделом; иначе строка остаётся строкой — текст
 * виден, галочка ставится, нажатие ничего не открывает.
 *
 * На сайте — как было: путь из задания как есть.
 */

/**
 * Разделы приложения, на которые может вести задание, → чем маршрут закрыт в `app/router.tsx`.
 * Пустой список — маршрут без гейта. Пути, которого здесь нет, для приложения не существует.
 *
 * Сторож `pages/student/scheduleLinks.test.ts` сверяет таблицу с роутером: путь зарегистрирован вне ветки
 * «только сайт», гейт тот же, и каждый раздел из списка куратора здесь учтён.
 */
export const APP_TASK_ROUTES: Readonly<Record<string, readonly FeatureKey[]>> = {
  '/app/vocabulary': ['vocab'],
  '/app/cue-cards': ['vocab'],
  '/app/homework': [],
  '/app/ielts/listening': ['ielts_listening'],
  '/app/ielts/reading': ['ielts_reading'],
  '/app/ielts/speaking': ['ielts_speaking'],
  '/app/ielts/writing': ['ielts_writing'],
};

export function taskLinkFor(
  task: Pick<DailyTask, 'kind' | 'target'>,
  user: AuthUser | null,
  app: boolean = IS_APP_BUILD,
): string | null {
  const link = taskLink(task);
  if (!link || !app) return link;
  // Урок: маршрут `/app/lessons/:id` в сборке есть и разделом не закрыт (на него же ведут пуши).
  if (task.kind === 'lesson') return link;
  const keys = APP_TASK_ROUTES[link];
  if (!keys) return null;
  // Закрытый вход (D22: разговор с ИИ) увёл бы на хаб тренажёров с объяснением — это не то место, куда вело
  // задание. В приложении такая строка тоже остаётся без перехода.
  if (entryHidden(link)) return null;
  // Учётка ещё не загружена — перехода нет: `computeHasFeature` для неё отвечает «да», и ссылка мелькнула бы.
  if (!user) return null;
  return keys.every((k) => computeHasFeature(user, k, true)) ? link : null;
}
